import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import SpinnerView from '../../components/spinner/spinner-view';
import Appointment from '../../components/appointment';
import '../../styles/calendar-glass.css';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MINI_DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const HOUR_START = 6;   // time-grid window
const HOUR_END = 22;
const HOUR_H = 48;      // px per hour

const initials = (nm) => (nm || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
const avColor = (nm) => { let h = 0; for (let c = 0; c < (nm || '').length; c++) h = (h * 31 + nm.charCodeAt(c)) % 6; return h; };

class Calendar extends Component {
  state = {
    current: moment(),     // focus date
    view: 'month',         // month | week | day | list
    showAdd: false,
    hoverKey: null,
  };

  componentDidMount() {
    this.props.dispatchGetActiveJobs();
  }

  componentDidUpdate(prevProps) {
    // On a hard refresh the mount fetch fires before the company (and its
    // selectedCompanyId) has loaded, so it returns nothing. Re-fetch once the
    // company first becomes available.
    const users = (this.props.company && this.props.company.users) || [];
    const prevUsers = (prevProps.company && prevProps.company.users) || [];
    if (users.length && !prevUsers.length && (!this.props.activeJobs || !this.props.activeJobs.length)) {
      this.props.dispatchGetActiveJobs();
    }
  }

  updateshowAdd = (showAdd) => this.setState({ showAdd });
  openAppointment = (job) => { this.props.dispatchSelectAppointment(job); this.setState({ showAdd: true }); };
  setView = (view) => this.setState({ view });

  shift = (delta) => {
    const unit = this.state.view === 'week' ? 'week' : this.state.view === 'day' ? 'day' : 'month';
    this.setState((s) => ({ current: s.current.clone().add(delta, unit) }));
  };
  goToday = () => this.setState({ current: moment() });

  // --- drag-drop scheduling ---
  onDragStart = (e, jobId) => e.dataTransfer.setData('jobId', jobId);
  scheduleJob = (jobId, startMoment) => {
    const job = (this.props.activeJobs || []).find((j) => j._id == jobId);
    if (!job) return;
    const orig = job.startDate ? moment(job.startDate) : null;
    const dur = (orig && job.endDate) ? moment(job.endDate).diff(orig, 'minutes') : 60;
    job.startDate = startMoment.toISOString();
    job.endDate = startMoment.clone().add(dur > 0 ? dur : 60, 'minutes').toISOString();
    job.hasSchedule = true;
    if (!job.status) job.status = 'Scheduled';
    this.props.dispatchSetAppointment(job);
  };
  onDayDrop = (e, dayMoment) => {
    e.preventDefault();
    this.setState({ hoverKey: null });
    const jobId = e.dataTransfer.getData('jobId');
    const orig = (this.props.activeJobs || []).find((j) => j._id == jobId);
    const o = orig && orig.startDate ? moment(orig.startDate) : null;
    this.scheduleJob(jobId, dayMoment.clone().hour(o ? o.hour() : 9).minute(o ? o.minute() : 0).second(0));
  };
  onSlotDrop = (e, dayMoment) => {
    e.preventDefault();
    this.setState({ hoverKey: null });
    const jobId = e.dataTransfer.getData('jobId');
    const offsetY = e.nativeEvent ? e.nativeEvent.offsetY : 0;
    const hour = Math.max(HOUR_START, Math.min(HOUR_END - 1, HOUR_START + Math.floor(offsetY / HOUR_H)));
    this.scheduleJob(jobId, dayMoment.clone().hour(hour).minute(0).second(0));
  };

  classify = (job) => {
    if (job.isAllDayEvent) return 'allday';
    if (job.startDate && moment(job.startDate) < moment()) return 'attention';
    return 'scheduled';
  };

  // ---- data ----
  buildData() {
    const jobs = this.props.activeJobs || [];
    const scheduled = jobs.filter((j) => (j.hasSchedule === undefined || j.hasSchedule === true) && j.startDate);
    const unscheduled = jobs.filter((j) => !(j.hasSchedule === undefined || j.hasSchedule === true));
    const byDay = {};
    scheduled.forEach((j) => { const k = moment(j.startDate).format('YYYY-MM-DD'); (byDay[k] = byDay[k] || []).push(j); });
    Object.keys(byDay).forEach((k) => byDay[k].sort((a, b) => (moment(a.startDate) < moment(b.startDate) ? -1 : 1)));
    return { scheduled, unscheduled, byDay };
  }

  heroLabel() {
    const c = this.state.current;
    if (this.state.view === 'week') {
      const s = c.clone().startOf('week'), e = c.clone().endOf('week');
      return s.month() === e.month() ? `${s.format('MMM D')} – ${e.format('D, YYYY')}` : `${s.format('MMM D')} – ${e.format('MMM D')}`;
    }
    if (this.state.view === 'day') return c.format('ddd, D MMM YYYY');
    return c.format('MMMM YYYY');
  }

  // ---- MONTH ----
  renderMonth(byDay) {
    const monthStart = this.state.current.clone().startOf('month');
    const gridStart = monthStart.clone().startOf('week');
    const today = moment();
    const cells = [];
    for (let i = 0; i < 42; i++) cells.push(gridStart.clone().add(i, 'days'));
    const rows = cells[35].isSame(monthStart, 'month') || cells[35].isAfter(monthStart, 'month') ? 6 : 5;
    const visible = cells.slice(0, rows * 7);
    const MAX = 3;
    return (
      <div className="cal-panel">
        <div className="cal-head">{DOW.map((d, i) => <div key={d} className={`cal-dow ${i === 0 || i === 6 ? 'weekend' : ''}`}>{d}</div>)}</div>
        <div className="cal-grid">
          {visible.map((d) => {
            const key = d.format('YYYY-MM-DD');
            const inMonth = d.isSame(monthStart, 'month');
            const isToday = d.isSame(today, 'day');
            const weekend = d.day() === 0 || d.day() === 6;
            const evs = byDay[key] || [];
            return (
              <div key={key}
                className={`cal-cell ${inMonth ? '' : 'other'} ${isToday ? 'today' : ''} ${weekend ? 'weekend' : ''} ${this.state.hoverKey === key ? 'drop-target' : ''}`}
                onDragOver={(e) => { e.preventDefault(); if (this.state.hoverKey !== key) this.setState({ hoverKey: key }); }}
                onDragLeave={() => this.state.hoverKey === key && this.setState({ hoverKey: null })}
                onDrop={(e) => this.onDayDrop(e, d)}
                onClick={() => { this.setState({ current: d.clone(), view: 'day' }); }}>
                <div className="cal-dayhead"><span className="cal-daynum">{d.date()}</span>{isToday && <span className="cal-daymeta">Today</span>}</div>
                {evs.slice(0, MAX).map((j) => (
                  <div key={j._id} className={`cal-ev ${this.classify(j)}`} draggable
                    onDragStart={(e) => { e.stopPropagation(); this.onDragStart(e, j._id); }}
                    onClick={(e) => { e.stopPropagation(); this.openAppointment(j); }} title={j.clientName}>
                    {!j.isAllDayEvent && <span className="time">{moment(j.startDate).format('HH:mm')}</span>}
                    <span className="name">{j.clientName || j.appointmentType}</span>
                  </div>
                ))}
                {evs.length > MAX && <div className="cal-ev more" onClick={(e) => { e.stopPropagation(); this.setState({ current: d.clone(), view: 'day' }); }}>+ {evs.length - MAX} more</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- WEEK / DAY (time grid) ----
  renderTimeGrid(byDay, days) {
    const today = moment();
    const hours = [];
    for (let h = HOUR_START; h < HOUR_END; h++) hours.push(h);
    const cols = days.length;
    const evTop = (m) => (m.hour() + m.minute() / 60 - HOUR_START) * HOUR_H;
    return (
      <div className="cal-panel">
        <div className="cal-timegrid" style={{ ['--cols']: cols }}>
          {/* day headers */}
          <div className="cal-tg-head" style={{ ['--cols']: cols }}>
            <div className="cal-tg-corner"></div>
            {days.map((d) => {
              const isToday = d.isSame(today, 'day');
              const weekend = d.day() === 0 || d.day() === 6;
              return (
                <div key={d.format('YYYY-MM-DD')} className={`cal-tg-dayhead ${isToday ? 'today' : ''} ${weekend ? 'weekend' : ''}`}>
                  <div className="dow">{DOW[d.day()]}</div>
                  <div className="dnum">{d.date()}</div>
                </div>
              );
            })}
          </div>
          {/* all-day row */}
          <div className="cal-tg-allday-row" style={{ ['--cols']: cols }}>
            <div className="cal-tg-allday-label">All-day</div>
            {days.map((d) => {
              const evs = (byDay[d.format('YYYY-MM-DD')] || []).filter((j) => j.isAllDayEvent);
              return (
                <div key={'ad' + d.format('YYYY-MM-DD')} className="cal-tg-allday-cell"
                  onDragOver={(e) => e.preventDefault()} onDrop={(e) => this.onDayDrop(e, d)}>
                  {evs.map((j) => <div key={j._id} className="cal-ev allday" style={{ position: 'static' }} onClick={() => this.openAppointment(j)}>{j.clientName || j.appointmentType}</div>)}
                </div>
              );
            })}
          </div>
          {/* scrollable time grid */}
          <div className="cal-tg-scroll">
            <div className="cal-tg-grid" style={{ ['--cols']: cols }}>
              <div className="cal-tg-gutter">{hours.map((h) => <div key={h} className="cal-tg-hourlabel">{moment().hour(h).minute(0).format('HH:mm')}</div>)}</div>
              {days.map((d) => {
                const isToday = d.isSame(today, 'day');
                const weekend = d.day() === 0 || d.day() === 6;
                const evs = (byDay[d.format('YYYY-MM-DD')] || []).filter((j) => !j.isAllDayEvent);
                const nowTop = isToday && today.hour() >= HOUR_START && today.hour() < HOUR_END ? evTop(today) : null;
                return (
                  <div key={'col' + d.format('YYYY-MM-DD')} className={`cal-tg-col ${isToday ? 'today' : ''} ${weekend ? 'weekend' : ''}`}
                    onDragOver={(e) => e.preventDefault()} onDrop={(e) => this.onSlotDrop(e, d)}
                    onClick={() => this.openAppointment(undefined)}>
                    {hours.map((h) => <div key={h} className="cal-tg-hourline"></div>)}
                    {nowTop != null && <div className="cal-tg-now" style={{ top: nowTop }}></div>}
                    {evs.map((j) => {
                      const s = moment(j.startDate);
                      const e = j.endDate ? moment(j.endDate) : s.clone().add(1, 'hour');
                      const top = Math.max(0, evTop(s));
                      const h = Math.max(22, (e.diff(s, 'minutes') / 60) * HOUR_H);
                      return (
                        <div key={j._id} className={`cal-tg-ev ${this.classify(j)}`} style={{ top, height: h }} draggable
                          onDragStart={(ev) => { ev.stopPropagation(); this.onDragStart(ev, j._id); }}
                          onClick={(ev) => { ev.stopPropagation(); this.openAppointment(j); }} title={j.clientName}>
                          <div className="t">{s.format('HH:mm')}</div>
                          <div className="name">{j.clientName || j.appointmentType}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- LIST ----
  renderList(scheduled) {
    const from = this.state.current.clone().startOf('month');
    const items = scheduled
      .filter((j) => moment(j.startDate).isSameOrAfter(from, 'day'))
      .sort((a, b) => (moment(a.startDate) < moment(b.startDate) ? -1 : 1));
    const groups = {};
    items.forEach((j) => { const k = moment(j.startDate).format('YYYY-MM-DD'); (groups[k] = groups[k] || []).push(j); });
    const today = moment();
    const keys = Object.keys(groups).sort();
    return (
      <div className="cal-panel">
        <div className="cal-list">
          {keys.length === 0 && <div className="cal-list-empty">No upcoming appointments.</div>}
          {keys.map((k) => {
            const d = moment(k);
            return (
              <div key={k} className="cal-list-day">
                <div className={`cal-list-dayhead ${d.isSame(today, 'day') ? 'today' : ''}`}>
                  <span className="dn">{d.format('ddd D')}</span><span className="dm">{d.format('MMMM YYYY')}</span>
                </div>
                {groups[k].map((j) => (
                  <div key={j._id} className="cal-list-row" onClick={() => this.openAppointment(j)}>
                    <div className="cal-list-time">{j.isAllDayEvent ? 'All-day' : moment(j.startDate).format('HH:mm')}</div>
                    <div className={`cal-list-bar ${this.classify(j)}`}></div>
                    <div className="cal-list-main">
                      <div className="name">{j.clientName || j.appointmentType}</div>
                      <div className="sub">{j.appointmentType}{j.faultDescription ? ' · ' + j.faultDescription.substring(0, 40) : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  render() {
    const { scheduled, unscheduled, byDay } = this.buildData();
    const today = moment();
    const view = this.state.view;

    // mini-cal grid (always the focus month)
    const monthStart = this.state.current.clone().startOf('month');
    const miniStart = monthStart.clone().startOf('week');
    const miniCells = [];
    for (let i = 0; i < 42; i++) miniCells.push(miniStart.clone().add(i, 'days'));

    let main = null;
    if (view === 'month') main = this.renderMonth(byDay);
    else if (view === 'week') main = this.renderTimeGrid(byDay, Array.from({ length: 7 }, (_, i) => this.state.current.clone().startOf('week').add(i, 'days')));
    else if (view === 'day') main = this.renderTimeGrid(byDay, [this.state.current.clone()]);
    else main = this.renderList(scheduled);

    const TABS = [['month', 'Month'], ['week', 'Week'], ['day', 'Day'], ['list', 'List']];

    return (
      <div className="content-wrapper accent-calendar">
        <SpinnerView showSpinner={this.props.loading} />
        {this.state.showAdd && <Appointment updateshowAdd={this.updateshowAdd} />}

        {!this.state.showAdd && (
          <div style={{ padding: '4px 6px 0', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 96px)', overflowX: 'hidden', minWidth: 0 }}>
            {/* Hero */}
            <div className="cal-hero">
              <div className="cal-hero-row">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                    <div className="cal-hero-title">Calendar</div>
                    <div className="cal-datenav">
                      <div className="cal-date-arrow" onClick={() => this.shift(-1)}><i className="fas fa-chevron-left"></i></div>
                      <div className="cal-today-btn" onClick={this.goToday}>Today</div>
                      <div className="cal-date-arrow" onClick={() => this.shift(1)}><i className="fas fa-chevron-right"></i></div>
                      <div className="cal-month-label">{this.heroLabel()}</div>
                    </div>
                  </div>
                  <div className="cal-hero-sub">
                    <span className="cal-chip-stat"><i className="fas fa-circle" style={{ fontSize: 5 }}></i> {scheduled.length} scheduled</span>
                    <span className="cal-chip-stat"><i className="fas fa-clock-rotate-left" style={{ fontSize: 8 }}></i> {unscheduled.length} unscheduled</span>
                  </div>
                </div>
                <div className="cal-hero-actions">
                  <div className="cal-view-toggle">
                    {TABS.map(([v, label]) => <button key={v} className={view === v ? 'active' : ''} onClick={() => this.setView(v)}>{label}</button>)}
                  </div>
                  <Link to="/statuses" className="cal-ghost-btn" title="Switch to the Job Board"><i className="fas fa-table-columns"></i> Job Board</Link>
                  <button className="cal-add-btn" onClick={() => this.openAppointment(undefined)}><i className="fas fa-plus"></i> New appointment</button>
                </div>
              </div>
            </div>

            {/* Body: rail + main */}
            <div className="cal-body-grid">
              {/* RAIL */}
              <div className="cal-rail">
                <div className="cal-railcard">
                  <div className="cal-mini-head">
                    <div className="cal-mini-title">{this.state.current.format('MMMM YYYY')}</div>
                    <div className="cal-mini-arrows">
                      <div className="cal-mini-arrow" onClick={() => this.setState((s) => ({ current: s.current.clone().add(-1, 'month') }))}><i className="fas fa-chevron-left"></i></div>
                      <div className="cal-mini-arrow" onClick={() => this.setState((s) => ({ current: s.current.clone().add(1, 'month') }))}><i className="fas fa-chevron-right"></i></div>
                    </div>
                  </div>
                  <div className="cal-mini-grid">
                    {MINI_DOW.map((d, i) => <div key={'md' + i} className="cal-mini-dow">{d}</div>)}
                    {miniCells.map((d) => {
                      const inMonth = d.isSame(monthStart, 'month');
                      const isToday = d.isSame(today, 'day');
                      const isSel = d.isSame(this.state.current, 'day');
                      const has = !!byDay[d.format('YYYY-MM-DD')];
                      return (
                        <div key={'m' + d.format('YYYY-MM-DD')}
                          className={`cal-mini-day ${inMonth ? '' : 'other'} ${isToday ? 'today' : ''} ${!isToday && isSel ? 'selected' : ''}`}
                          onClick={() => this.setState({ current: d.clone(), view: view === 'month' ? 'day' : view })}>
                          {d.date()}{has && !isToday && <span className="cal-mini-dot"></span>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="cal-railcard" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <h4>Unscheduled <span className="pill-count">{unscheduled.length}</span></h4>
                  <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, margin: '0 -4px', padding: '0 4px' }}>
                    {unscheduled.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '8px 2px' }}>Nothing to schedule.</div>}
                    {unscheduled.map((j) => (
                      <div key={j._id} className="cal-unsched" draggable onDragStart={(e) => this.onDragStart(e, j._id)} onClick={() => this.openAppointment(j)}>
                        <div className={`cal-unsched-av cal-av-${avColor(j.clientName)}`}>{initials(j.clientName)}</div>
                        <div className="cal-unsched-info">
                          <div className="name">{j.clientName || '—'}</div>
                          <div className="sub">{j.appointmentType}{j.faultDescription ? ' · ' + j.faultDescription.substring(0, 24) : ''}</div>
                        </div>
                        <i className="fas fa-grip-vertical" style={{ color: 'var(--text-muted)', fontSize: 11 }}></i>
                      </div>
                    ))}
                  </div>
                  <div className="cal-unsched-hint"><i className="fas fa-circle-info"></i> Drag onto the calendar to schedule</div>
                </div>

                <div className="cal-railcard">
                  <h4>Legend</h4>
                  <div className="cal-legend-row"><div className="cal-legend-dot sc"></div>Scheduled</div>
                  <div className="cal-legend-row"><div className="cal-legend-dot at"></div>Overdue</div>
                  <div className="cal-legend-row"><div className="cal-legend-dot ad"></div>All-day</div>
                </div>
              </div>

              {/* MAIN */}
              {main}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export { Calendar };
